import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Repeat } from "lucide-react";
import { ROUTES } from "../../routes";

interface TimeSlotProps {
  time: string;
  selectedDate?: Date;
  duration?: number;
  disabled?: boolean;
  /** The slot is also free at the same weekday and time the coming weeks. */
  isWeekly?: boolean;
}

const TimeSlot: React.FC<TimeSlotProps> = ({
  time,
  selectedDate,
  duration,
  disabled = false,
  isWeekly = false,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleClick = () => {
    if (disabled) return;

    // Extract the start time from the displayed time range (e.g., "12:00 PM - 1:00 PM")
    const displayedStartTime = time.split(" - ")[0]; // Gets "12:00 PM"
    
    // Convert 12-hour format to 24-hour format
    const convertTo24Hour = (time12h: string): string => {
      const [time, modifier] = time12h.split(" ");
      let [hours, minutes] = time.split(":");
      
      if (hours === "12") {
        hours = "00";
      }
      
      if (modifier === "PM") {
        hours = (parseInt(hours, 10) + 12).toString();
      }
      
      return `${hours.padStart(2, "0")}:${minutes}`;
    };
    
    const startTime24h = convertTo24Hour(displayedStartTime);
     
     // Navigate to create session page with state
    navigate(ROUTES.CREATE_SESSION, {
      state: {
        selectedDate: selectedDate || new Date(),
        startTime: startTime24h, // Pass converted 24-hour format
        duration: duration,
      },
    });
    // BrowserRouter preserves the scroll position from the previous page.
    // Available time slots sit below the fold on Home, so without this the
    // create-session page renders scrolled past the calendar.
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  return (
    <button
      className={`flex w-full ${
        disabled ? "cursor-not-allowed" : "cursor-pointer"
      }`}
      onClick={handleClick}
      disabled={disabled}
      type="button"
    >
      {/* checkbox-group-item is a flex row; stack time + badge as a column and
          let the range break only at the dash, never inside "9:00 AM". */}
      <span className="checkbox-group-item w-full flex-col">
        <span className="leading-tight">
          {time.split(" - ").map((part, i) => (
            <React.Fragment key={part}>
              {i > 0 && " - "}
              <span className="whitespace-nowrap">{part}</span>
            </React.Fragment>
          ))}
        </span>
        {isWeekly && (
          <span className="mt-0.5 flex items-center justify-center gap-1 text-xs font-medium text-primary-40 dark:text-primary-90">
            <Repeat size={12} strokeWidth={2.5} />
            {t("sessions.timeSlotsPage.weekly")}
          </span>
        )}
      </span>
    </button>
  );
};

export default TimeSlot;
